import { Dropdown, Table, Button, Form } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import ReactPaginate from "react-paginate";
import { PropagateLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "../hooks/LanguageProvider";

type RFQStatus = "submitted" | "shortlisted" | "rejected" | "accepted";

interface RFQItem {
  [x: string]: any;
  id: number;
  rfq_id: number;
  rfq_title: string;

  oem: {
    id: number;
    name: string;
    logo?: string;
    country: string;
  };

  supplier: {
    id: number;
    name: string;
    logo?: string;
    maturity_score: number;
    country: string;
    total_for_rfq: number;
  };

  price: number;
  currency: "EUR" | "USD" | "DZD";
  delivery_time_days: number;
  capacity: number;
  compliance_score: number;

  messages_count: number;
  documents_count: number;

  submitted_at: string;
  updated_at: string;

  status: RFQStatus;
  ai_score: number;
}

const fakeRFQs: RFQItem[] = [
  {
    id: 1,
    rfq_id: 101,
    rfq_title: "Injection plastique - Tableau de bord",
    oem: { id: 1, name: "Renault Algérie", country: "Algeria" },
    supplier: {
        id: 12,
        name: "PlastTech DZ",
        maturity_score: 78,
        country: "Algeria",
        total_for_rfq: 5
    },
    price: 125000,
    currency: "EUR",
    delivery_time_days: 30,
    capacity: 5000,
    compliance_score: 85,
    messages_count: 6,
    documents_count: 3,
    submitted_at: "2026-04-10",
    updated_at: "2026-04-11",
    status: "shortlisted",
    ai_score: 88,
  },
  {
    id: 2,
    rfq_id: 101,
    rfq_title: "Injection plastique - Tableau de bord",
    oem: { id: 1, name: "Renault Algérie", country: "Algeria" },
    supplier: {
        id: 15,
        name: "Global Plastics",
        maturity_score: 92,
        country: "Turkey",
        total_for_rfq: 25
    },
    price: 118000,
    currency: "EUR",
    delivery_time_days: 45,
    capacity: 7000,
    compliance_score: 90,
    messages_count: 2,
    documents_count: 5,
    submitted_at: "2026-04-09",
    updated_at: "2026-04-10",
    status: "submitted",
    ai_score: 91,
  },
  {
    id: 3,
    rfq_id: 102,
    rfq_title: "Câblage automobile série",
    oem: { id: 2, name: "Stellantis", country: "Algeria" },
    supplier: {
        id: 18,
        name: "AutoWire DZ",
        maturity_score: 65,
        country: "Algeria",
        total_for_rfq: 12
    },
    price: 89000,
    currency: "EUR",
    delivery_time_days: 25,
    capacity: 3000,
    compliance_score: 70,
    messages_count: 10,
    documents_count: 1,
    submitted_at: "2026-04-08",
    updated_at: "2026-04-10",
    status: "rejected",
    ai_score: 68,
  },
  {
    id: 4,
    rfq_id: 103,
    rfq_title: "Pièces métalliques embouties",
    oem: { id: 3, name: "FIAT Algeria", country: "Algeria" },
    supplier: {
        id: 22,
        name: "MetalForm Pro",
        maturity_score: 84,
        country: "Tunisia",
        total_for_rfq: 55
    },
    price: 97000,
    currency: "EUR",
    delivery_time_days: 18,
    capacity: 8500,
    compliance_score: 93,
    messages_count: 4,
    documents_count: 4,
    submitted_at: "2026-04-11",
    updated_at: "2026-04-11",
    status: "accepted",
    ai_score: 95,
  },
  {
    id: 5,
    rfq_id: 104,
    rfq_title: "Faisceaux électriques - plateforme B",
    oem: { id: 4, name: "Hyundai Algeria", country: "Algeria" },
    supplier: {
        id: 27,
        name: "CableTech Industrie",
        maturity_score: 72,
        country: "Algeria",
        total_for_rfq: 5
    },
    price: 76000,
    currency: "EUR",
    delivery_time_days: 22,
    capacity: 4200,
    compliance_score: 79,
    messages_count: 7,
    documents_count: 2,
    submitted_at: "2026-04-07",
    updated_at: "2026-04-09",
    status: "submitted",
    ai_score: 81,
  },
  {
    id: 6,
    rfq_id: 105,
    rfq_title: "Composants plastiques intérieurs",
    oem: { id: 5, name: "JAC Motors Algeria", country: "Algeria" },
    supplier: {
        id: 29,
        name: "Polymer Works",
        maturity_score: 88,
        country: "Spain",
        total_for_rfq: 2
    },
    price: 133500,
    currency: "EUR",
    delivery_time_days: 35,
    capacity: 6200,
    compliance_score: 87,
    messages_count: 3,
    documents_count: 6,
    submitted_at: "2026-04-06",
    updated_at: "2026-04-08",
    status: "shortlisted",
    ai_score: 89,
  },
];

export default function RFQList() {
  const navigate = useNavigate();
  const { translate } = useTranslate();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<RFQItem[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | RFQStatus>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limitValue, setLimitValue] = useState(10);

  const [sortColumn, setSortColumn] = useState<keyof RFQItem | "oem" | "supplier">("submitted_at");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  useEffect(() => {
    const timer = setTimeout(() => {
      setItems(fakeRFQs);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const formatPrice = (value: number, currency: string) => {
    return new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 0,
    }).format(value) + ` ${currency}`;
  };

  const getStatusBadge = (status: RFQStatus) => {
    const config = {
      submitted: {
        label: "Submitted",
        bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
        color: "#1d4ed8",
        border: "#93c5fd",
        dot: "#3b82f6",
      },
      shortlisted: {
        label: "Shortlisted",
        bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
        color: "#b45309",
        border: "#fcd34d",
        dot: "#f59e0b",
      },
      rejected: {
        label: "Rejected",
        bg: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
        color: "#be123c",
        border: "#fda4af",
        dot: "#f43f5e",
      },
      accepted: {
        label: "Accepted",
        bg: "linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%)",
        color: "#166534",
        border: "#86efac",
        dot: "#22c55e",
      },
    };

    const current = config[status];

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 14px",
          borderRadius: "999px",
          background: current.bg,
          color: current.color,
          border: `1px solid ${current.border}`,
          fontSize: "12px",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: current.dot,
          }}
        />
        {current.label}
      </span>
    );
  };

  const getScoreBadge = (score: number) => {
    let bg = "#f3f4f6";
    let color = "#374151";

    if (score >= 90) {
      bg = "#dcfce7";
      color = "#166534";
    } else if (score >= 75) {
      bg = "#fef3c7";
      color = "#92400e";
    } else {
      bg = "#fee2e2";
      color = "#991b1b";
    }

    return (
      <span
        style={{
          display: "inline-block",
          minWidth: "56px",
          textAlign: "center",
          padding: "6px 10px",
          borderRadius: "10px",
          background: bg,
          color,
          fontWeight: 700,
          fontSize: "12px",
        }}
      >
        {score}%
      </span>
    );
  };

  const filteredAndSorted = useMemo(() => {
    let data = [...items];

    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((item) =>
        [
          item.rfq_title,
          item.oem.name,
          item.supplier.name,
          item.oem.country,
          item.supplier.country,
          item.status,
          item.total_for_rfq
          
        ]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    if (statusFilter !== "all") {
      data = data.filter((item) => item.status === statusFilter);
    }

    data.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "oem":
          aValue = a.oem.name;
          bValue = b.oem.name;
          break;
        case "supplier":
          aValue = a.supplier.name;
          bValue = b.supplier.name;
          break;
        default:
          aValue = a[sortColumn as keyof RFQItem];
          bValue = b[sortColumn as keyof RFQItem];
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "ASC"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === "ASC"
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    });

    return data;
  }, [items, search, statusFilter, sortColumn, sortDirection]);

  const pageCount = Math.ceil(filteredAndSorted.length / limitValue);
  const paginatedItems = filteredAndSorted.slice(
    (currentPage - 1) * limitValue,
    currentPage * limitValue
  );

  const handleSortingColumn = (column: keyof RFQItem | "oem" | "supplier") => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortColumn(column);
      setSortDirection("ASC");
    }
  };

  const handlePageClick = (data: { selected: number }) => {
    setCurrentPage(data.selected + 1);
    window.scrollTo(0, 0);
  };

  const scoreCard = (title: string, value: number | string, icon: string, bg: string) => (
    <div className="col-md-3 mb-3">
      <div
        className="card border-0 shadow-sm h-100"
        style={{ borderRadius: "18px", overflow: "hidden" }}
      >
        <div className="card-body d-flex align-items-center justify-content-between">
          <div>
            <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: "24px", fontWeight: 700, color: "#111827" }}>{value}</div>
          </div>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#111827",
              fontSize: 22,
            }}
          >
            <i className={icon}></i>
          </div>
        </div>
      </div>
    </div>
  );

  const totalSubmitted = items.length;
  const totalAccepted = items.filter((i) => i.status === "accepted").length;
  const avgAiScore =
    items.length > 0
      ? Math.round(items.reduce((sum, item) => sum + item.ai_score, 0) / items.length)
      : 0;
  const totalMessages = items.reduce((sum, item) => sum + item.messages_count, 0);

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        {scoreCard("Total RFQs", totalSubmitted, "fas fa-file-signature", "#fef3c7")}
        {scoreCard("Accepted", totalAccepted, "fas fa-circle-check", "#dcfce7")}
        {scoreCard("Average AI Score", `${avgAiScore}%`, "fas fa-star", "#dbeafe")}
        {scoreCard("Messages", totalMessages, "fas fa-comments", "#ede9fe")}
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <h4 className="mb-0">
            <i className="las la-file-invoice-dollar me-2"></i>
            {translate("RFQs")} ({filteredAndSorted.length})
          </h4>
          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
            Supplier offers submitted on OEM RFQs
          </div>
        </div>

        <div className="col-md-6 text-end">
          <Button
            variant="warning"
            onClick={() => navigate("/new-rfq")}
            style={{ fontWeight: 600 }}
          >
            + New RFQ
          </Button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "18px" }}>
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-5">
              <Form.Control
                type="text"
                placeholder="Search by RFQ, OEM, supplier..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="col-md-3">
              <Form.Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as "all" | RFQStatus);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All status</option>
                <option value="submitted">Submitted</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </Form.Select>
            </div>

            <div className="col-md-2">
              <Form.Select
                value={limitValue}
                onChange={(e) => {
                  setLimitValue(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>

            <div className="col-md-2 text-end">
              <Dropdown>
                <Dropdown.Toggle variant="light">
                  <i className="las la-filter me-1"></i>
                  Sort
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleSortingColumn("submitted_at")}>
                    Submitted date
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortingColumn("price")}>
                    Price
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortingColumn("ai_score")}>
                    AI Score
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortingColumn("compliance_score")}>
                    Compliance
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortingColumn("delivery_time_days")}>
                    Delivery time
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>

      <Table responsive hover className="dataTable role-table mt-3 align-middle">
        <thead>
          <tr className="ligth ligth-data">
            <th onClick={() => handleSortingColumn("rfq_title")} style={{ cursor: "pointer" }}>
              RFQ
            </th>
            <th onClick={() => handleSortingColumn("oem")} style={{ cursor: "pointer" }}>
              OEM
            </th>
            <th onClick={() => handleSortingColumn("supplier")} style={{ cursor: "pointer" }}>
              Supplier
            </th>
            <th onClick={() => handleSortingColumn("price")} style={{ cursor: "pointer" }}>
              Price
            </th>
            <th onClick={() => handleSortingColumn("delivery_time_days")} style={{ cursor: "pointer" }}>
              Delivery
            </th>
            <th onClick={() => handleSortingColumn("capacity")} style={{ cursor: "pointer" }}>
              Capacity
            </th>
            <th onClick={() => handleSortingColumn("compliance_score")} style={{ cursor: "pointer" }}>
              Compliance
            </th>
            <th onClick={() => handleSortingColumn("ai_score")} style={{ cursor: "pointer" }}>
              AI Score
            </th>
            <th>Activity</th>
            <th>Status</th>
            <th onClick={() => handleSortingColumn("submitted_at")} style={{ cursor: "pointer" }}>
              Submitted
            </th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody className="ligth-body">
          {loading ? (
            <tr>
              <td className="text-center" colSpan={12}>
                <PropagateLoader size={12} />
              </td>
            </tr>
          ) : paginatedItems.length > 0 ? (
            paginatedItems.map((item) => (
              <tr key={item.id}>
                <td>
                  <div style={{ minWidth: 220 }}>
                    <div style={{ fontWeight: 700, color: "#111827" }}>{item.rfq_title}</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>RFQ #{item.rfq_id}</div>
                  </div>
                </td>

                <td>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.oem.name}</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>{item.oem.country}</div>
                  </div>
                </td>

                <td>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.supplier.total_for_rfq}</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    
                    </div>
                  </div>
                </td>

                <td style={{ fontWeight: 700 }}>{formatPrice(item.price, item.currency)}</td>
                <td>{item.delivery_time_days} days</td>
                <td>{item.capacity.toLocaleString("fr-FR")}</td>
                <td>{getScoreBadge(item.compliance_score)}</td>
                <td>{getScoreBadge(item.ai_score)}</td>

                <td>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span className="badge bg-light text-dark">
                      <i className="fas fa-comments me-1"></i>
                      {item.messages_count}
                    </span>
                    <span className="badge bg-light text-dark">
                      <i className="fas fa-paperclip me-1"></i>
                      {item.documents_count}
                    </span>
                  </div>
                </td>

                <td>{getStatusBadge(item.status)}</td>
                <td>{item.submitted_at}</td>

                <td className="text-center">
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn btn-sm rounded-circle"
                      style={{
                        width: 38,
                        height: 38,
                        backgroundColor: "#020815",
                        border: "1px solid #020815",
                        color: "#ffffff",
                      }}
                      title="View RFQ"
                    >
                      <i className="fas fa-eye"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-warning rounded-circle"
                      style={{ width: 38, height: 38 }}
                      title="Open discussion"
                    >
                      <i className="fas fa-comments"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-success rounded-circle"
                      style={{ width: 38, height: 38 }}
                      title="Shortlist / Accept"
                    >
                      <i className="fas fa-check"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={12} className="text-center">
                No RFQs available
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between mt-3 align-items-center">
        <span>
          {translate("Displaying")} {paginatedItems.length} {translate("out of")}{" "}
          {filteredAndSorted.length}
        </span>

        <ReactPaginate
          previousLabel={translate("previous")}
          nextLabel={translate("next")}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={handlePageClick}
          containerClassName={"pagination justify-content-end mb-0"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          breakClassName={"page-item"}
          breakLinkClassName={"page-link"}
          activeClassName={"active"}
          forcePage={currentPage - 1}
        />
      </div>
    </div>
  );
}